/**
 * @documental
 * @export Spinner
 * @import
 * import { Spinner } from "capitalrx-components-2.0/lib/general";
 */
import { FC, ReactNode, RefCallback } from "react";

import { BaseProps } from "capitalrx-components-2.0/lib/BaseProps";
import { getHtmlPropsFromBaseProps } from "capitalrx-components-2.0/lib/utilities/getHtmlPropsFromBaseProps";
import { AccessibilityProps } from "capitalrx-components-2.0/lib/AccessibilityProps";
import { SpinnerColor } from "./SpinnerColor";
import { SpinnerSize } from "./SpinnerSize";
import styles from "./styles.module.scss";
import { Tooltip } from "../Tooltip";

/**

/**
 * The `Spinner` component properties.
 */
export interface SpinnerProps extends BaseProps, Pick<AccessibilityProps, "aria-label" | "aria-busy" > {
  /**
   * The size of the spinner. If not specified defaults to `"regular"`.
   */
  readonly size?: SpinnerSize;

  /**
   * The spinner color. If not specified defaults to `"black"`.
   */
  readonly color?: SpinnerColor;

  /**
   * The spinner tooltip.
   */
  readonly tooltip?: ReactNode;
}

const SpinnerSvg: FC = () => (
  <svg width="100%" height="100%" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <title>Loading Spinner</title>
    <g transform="matrix(0.2,0,0,0.2,0,0)">
      <path
        d="M63.75,3.75C63.75,1.68 62.07,0 60,0C57.93,0 56.25,1.68 56.25,3.75L56.25,26.25C56.25,28.32 57.93,30 60,30C62.07,30 63.75,28.32 63.75,26.25L63.75,3.75Z"
        fill="currentColor"
      />
    </g>
    <g opacity="0.52">
      <g transform="matrix(0.2,0,0,0.2,0,0)">
        <path
          d="M63.75,93.75C63.75,91.68 62.07,90 60,90C57.93,90 56.25,91.68 56.25,93.75L56.25,116.25C56.25,118.32 57.93,120 60,120C62.07,120 63.75,118.32 63.75,116.25L63.75,93.75Z"
          fill="currentColor"
        />
      </g>
    </g>
    <g opacity="0.28">
      <g transform="matrix(-7.65714e-17,0.2,-0.2,-7.65714e-17,35.25,-12.75)">
        <path
          d="M127.5,60C127.5,57.93 125.82,56.25 123.75,56.25C121.68,56.25 120,57.93 120,60L120,82.5C120,84.57 121.68,86.25 123.75,86.25C125.82,86.25 127.5,84.57 127.5,82.5L127.5,60Z"
          fill="currentColor"
        />
      </g>
    </g>
    <g opacity="0.76">
      <g transform="matrix(-7.65714e-17,0.2,-0.2,-7.65714e-17,17.25,5.25)">
        <path
          d="M37.5,60C37.5,57.93 35.82,56.25 33.75,56.25C31.68,56.25 30,57.93 30,60L30,82.5C30,84.57 31.68,86.25 33.75,86.25C35.82,86.25 37.5,84.57 37.5,82.5L37.5,60Z"
          fill="currentColor"
        />
      </g>
    </g>
    <g opacity="0.12">
      <g transform="matrix(0.173205,0.1,-0.1,0.173205,2.94087,-8.51009)">
        <path
          d="M94.252,9.913C94.252,7.844 92.572,6.163 90.502,6.163C88.433,6.163 86.752,7.844 86.752,9.913L86.752,32.413C86.752,34.483 88.433,36.163 90.502,36.163C92.572,36.163 94.252,34.483 94.252,32.413L94.252,9.913Z"
          fill="currentColor"
        />
      </g>
    </g>
    <g opacity="0.6">
      <g transform="matrix(0.173205,0.1,-0.1,0.173205,9.52932,-1.92163)">
        <path
          d="M49.252,87.856C49.252,85.786 47.572,84.106 45.502,84.106C43.433,84.106 41.752,85.786 41.752,87.856L41.752,110.356C41.752,112.425 43.433,114.106 45.502,114.106C47.572,114.106 49.252,112.425 49.252,110.356L49.252,87.856Z"
          fill="currentColor"
        />
      </g>
    </g>
    <g opacity="0.36">
      <g transform="matrix(-0.1,0.173205,-0.173205,-0.1,49.1768,6.30875)">
        <path
          d="M121.336,90.502C121.336,88.433 119.656,86.752 117.586,86.752C115.516,86.752 113.836,88.433 113.836,90.502L113.836,113.002C113.836,115.072 115.516,116.752 117.586,116.752C119.656,116.752 121.336,115.072 121.336,113.002L121.336,90.502Z"
          fill="currentColor"
        />
      </g>
    </g>
    <g opacity="0.84">
      <g transform="matrix(-0.1,0.173205,-0.173205,-0.1,17.9999,6.3087)">
        <path
          d="M43.394,45.502C43.394,43.433 41.714,41.752 39.644,41.752C37.574,41.752 35.894,43.433 35.894,45.502L35.894,68.002C35.894,70.072 37.574,71.752 39.644,71.752C41.714,71.752 43.394,70.072 43.394,68.002L43.394,45.502Z"
          fill="currentColor"
        />
      </g>
    </g>
    <g opacity="0.2">
      <g transform="matrix(0.1,0.173205,-0.173205,0.1,15.6423,-16.3922)">
        <path
          d="M117.586,30.503C117.586,28.433 115.906,26.753 113.836,26.753C111.766,26.753 110.086,28.433 110.086,30.503L110.086,53.003C110.086,55.072 111.766,56.753 113.836,56.753C115.906,56.753 117.586,55.072 117.586,53.003L117.586,30.503Z"
          fill="currentColor"
        />
      </g>
    </g>
    <g opacity="0.68">
      <g transform="matrix(0.1,0.173205,-0.173205,0.1,15.6423,1.60774)">
        <path
          d="M39.644,75.502C39.644,73.433 37.964,71.752 35.894,71.752C33.824,71.752 32.144,73.433 32.144,75.502L32.144,98.002C32.144,100.072 33.824,101.752 35.894,101.752C37.964,101.752 39.644,100.072 39.644,98.002L39.644,75.502Z"
          fill="currentColor"
        />
      </g>
    </g>
    <g opacity="0.44">
      <g transform="matrix(-0.173205,0.1,-0.1,-0.173205,45.8092,31.7603)">
        <path
          d="M100.748,113.837C100.748,111.767 99.067,110.087 96.998,110.087C94.928,110.087 93.248,111.767 93.248,113.837L93.248,136.337C93.248,138.407 94.928,140.087 96.998,140.087C99.067,140.087 100.748,138.407 100.748,136.337L100.748,113.837Z"
          fill="currentColor"
        />
      </g>
    </g>
    <g opacity="0.92">
      <g transform="matrix(-0.173205,0.1,-0.1,-0.173205,21.2207,7.17166)">
        <path
          d="M55.748,35.894C55.748,33.825 54.067,32.144 51.998,32.144C49.928,32.144 48.248,33.825 48.248,35.894L48.248,58.394C48.248,60.464 49.928,62.144 51.998,62.144C54.067,62.144 55.748,60.464 55.748,58.394L55.748,35.894Z"
          fill="currentColor"
        />
      </g>
    </g>
  </svg>
);

/**
 * Displays a scalable spinner with the specified size and color.
 */
export const Spinner: FC<SpinnerProps> = (props) => {
  const { size = "regular", color = "black", tooltip, "aria-label": ariaLabel = "Loading", "aria-busy": ariaBusy = true } = props;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.Spinner,
      "spinner",
      "icon",
      color,
      `size-${size}`,
      {
        "has-tooltip": !!tooltip,
      },
    ],
    testIdPrefix: "spinner",
  });

  const content = (ref?: RefCallback<HTMLElement>) => (
    <span
      ref={ref}
      {...baseHtmlProps}
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
      aria-busy={ariaBusy}
    >
      <span className="spinner-container" aria-hidden="true">
        <SpinnerSvg />
      </span>
    </span>
  );

  return (
    <>
      {tooltip && <Tooltip content={tooltip}>{content}</Tooltip>}
      {!tooltip && content()}
    </>
  );
};
